import { MessageDescriptor, useIntl } from 'react-intl';
import { getDay } from 'date-fns';
import { Visit } from 'store/visits/types';
import { VisitorFrequencyEnum } from 'components/visit-form/types';
import { useDateTimeFormatters } from 'shared/hooks/use-date-time-formatters';
import { useLocale, STANDARD_DAY_KEYS, getLocalizedDayName, getDateFnsLocale } from 'shared/hooks/use-locale';
import { inferRepeatType } from 'components/create-visit-form-v2/context/create-visit-form-v2.context';
import { MonthlyMode, RepeatType } from 'components/create-visit-form-v2/types/form-data.types';
import {
  formatOrdinalEn,
  getMonthlyDayOfWeekDescriptor,
  isMonthlyOnLastDay,
  POSITION_MESSAGE_IDS,
} from 'components/create-visit-form-v2/steps/when-step/utils/monthly';

export interface ScheduleTextInput {
  frequency?: VisitorFrequencyEnum;
  recurrence_days?: Visit['recurrence_days'];
  arrival_date?: Date;
  end_date?: Date;
  count?: number;
  monthly_mode?: MonthlyMode | null;
  monthly_on_last_day?: boolean | null;
  monthly_by_day?: { position: number; weekday: string } | null;
}

export const useScheduleText = (visit: ScheduleTextInput | null): string | null => {
  const { formatMessage } = useIntl();
  const locale = useLocale();
  const dateFnsLocale = getDateFnsLocale(locale);
  const { formatDate: formatLongDate } = useDateTimeFormatters({
    dateFormat: { dateStyle: 'long', timeZone: 'UTC' },
  });

  if (!visit) {
    return null;
  }

  const repeatType = inferRepeatType(visit.frequency, visit.recurrence_days, visit.monthly_mode);

  const getMonthlyDayOfMonthLabel = (): string | null => {
    if (visit.monthly_on_last_day) {
      return formatMessage({
        id: 'createVisitFormV2.whenStep.repeat.monthlyOnLastDay',
        defaultMessage: 'Monthly on the last day of every month',
      });
    }
    const arrival = visit.arrival_date ? new Date(visit.arrival_date) : null;
    if (!arrival) {
      return null;
    }
    if (isMonthlyOnLastDay(arrival)) {
      return formatMessage({
        id: 'createVisitFormV2.whenStep.repeat.monthlyOnLastDay',
        defaultMessage: 'Monthly on the last day of every month',
      });
    }
    return formatMessage(
      {
        id: 'createVisitFormV2.whenStep.repeat.monthlyOnDate',
        defaultMessage: 'Monthly on the {dayOfMonth}',
      },
      { dayOfMonth: formatOrdinalEn(arrival.getDate()) },
    );
  };

  const getMonthlyDayOfWeekLabel = (): string | null => {
    const arrival = visit.arrival_date ? new Date(visit.arrival_date) : null;
    const position =
      visit.monthly_by_day?.position ?? (arrival ? getMonthlyDayOfWeekDescriptor(arrival).position : null);
    if (position === null || position === undefined || !arrival) {
      return null;
    }
    const dayKey = STANDARD_DAY_KEYS[getDay(arrival)];
    const weekdayName = getLocalizedDayName(dayKey, dateFnsLocale);
    if (position === -1) {
      return formatMessage(
        {
          id: 'createVisitFormV2.whenStep.repeat.monthlyOnLastWeekday',
          defaultMessage: 'Monthly on the last {weekday}',
        },
        { weekday: weekdayName },
      );
    }
    const positionId = POSITION_MESSAGE_IDS[position as 1 | 2 | 3 | 4];
    if (!positionId) {
      return null;
    }
    const positionLabel = formatMessage({ id: positionId } as MessageDescriptor);
    return formatMessage(
      {
        id: 'createVisitFormV2.whenStep.repeat.monthlyOnWeekday',
        defaultMessage: 'Monthly on the {position} {weekday}',
      },
      { position: positionLabel, weekday: weekdayName },
    );
  };

  const getRepeatLabel = (): string | null => {
    switch (repeatType) {
      case RepeatType.NONE:
        return null;
      case RepeatType.DAILY:
        return formatMessage({ id: 'createVisitFormV2.whenStep.repeat.daily', defaultMessage: 'Daily' });
      case RepeatType.WEEKDAY:
        return formatMessage({
          id: 'createVisitFormV2.whenStep.repeat.weekday',
          defaultMessage: 'Every weekday (Monday to Friday)',
        });
      case RepeatType.WEEKLY: {
        const activeDay =
          STANDARD_DAY_KEYS.find((d) => visit.recurrence_days?.[d]) ??
          STANDARD_DAY_KEYS[getDay(new Date(visit.arrival_date))];
        return formatMessage({
          id: `createVisitFormV2.whenStep.repeat.weeklyOn.${activeDay}`,
          defaultMessage: `Weekly on ${getLocalizedDayName(activeDay, dateFnsLocale)}`,
        });
      }
      case RepeatType.MONTHLY_DAY_OF_MONTH:
        return getMonthlyDayOfMonthLabel();
      case RepeatType.MONTHLY_DAY_OF_WEEK:
        return getMonthlyDayOfWeekLabel();
      case RepeatType.CUSTOM: {
        const activeDays = STANDARD_DAY_KEYS.filter((d) => visit.recurrence_days?.[d]);
        const names = activeDays.map((d) => getLocalizedDayName(d, dateFnsLocale, 'EEE')).join(', ');
        return `${formatMessage({
          id: 'createVisitFormV2.whenStep.repeat.custom',
          defaultMessage: 'Custom',
        })}: ${names}`;
      }
      default:
        return null;
    }
  };

  const repeatLabel = getRepeatLabel();
  if (!repeatLabel) {
    return null;
  }

  const endDate = visit.end_date ? new Date(visit.end_date as unknown as string) : undefined;

  if (visit.count) {
    return formatMessage({ id: 'labels.recurrence_count' }, { frequency: repeatLabel, count: visit.count });
  }
  if (endDate) {
    return formatMessage({ id: 'labels.recurrence_until' }, { frequency: repeatLabel, date: formatLongDate(endDate) });
  }
  return repeatLabel;
};
